import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  CircularProgress,
  IconButton,
  Paper,
  Divider
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import api from '../../utils/api';

interface Document {
  _id: string;
  title: string;
  content: string;
  required: boolean;
  order: number;
}

interface RecruitmentPackModalProps {
  open: boolean;
  onClose: () => void;
}

function RecruitmentPackModal({ open, onClose }: RecruitmentPackModalProps) {
  const [documents, setDocuments] = useState<Document[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchDocuments = async () => {
      if (!open) return;
      
      try {
        setLoading(true);
        const response = await api.get('/api/counsellors/public/pre-application-documents');
        setDocuments(response.data);
        setError('');
      } catch (err) {
        console.error('Error fetching documents:', err);
        setError('Failed to load recruitment pack documents');
      } finally {
        setLoading(false);
      }
    };

    fetchDocuments();
  }, [open]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '8px'
        }
      }}
    >
      <DialogTitle sx={{ borderBottom: '1px solid #e0e0e0', pb: 2 }}>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Typography variant="h5" sx={{ fontWeight: 600, color: 'primary.main' }}>
            Recruitment Pack
          </Typography>
          <IconButton
            onClick={onClose}
            sx={{ color: 'text.secondary' }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ mt: 2 }}>
        {loading ? (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Box textAlign="center" py={4}>
            <Typography color="error">{error}</Typography>
          </Box>
        ) : (
          <Box>
            {documents.map((doc, index) => (
              <Box key={doc._id} sx={{ mb: 3 }}>
                <Paper elevation={1} sx={{ p: 3, backgroundColor: '#f5f5f5' }}>
                  <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: 'primary.main', mb: 2 }}>
                    {doc.title}
                  </Typography>
                  <Box
                    sx={{ 
                      color: 'text.primary',
                      lineHeight: 1.7,
                      '& p': { mb: 2 },
                      '& ul, & ol': { pl: 3, mb: 2 },
                      '& li': { mb: 1 },
                      '& h1': { fontSize: '2rem', fontWeight: 700, mb: 2, mt: 2 },
                      '& h2': { fontSize: '1.5rem', fontWeight: 600, mb: 2, mt: 2 },
                      '& h3': { fontSize: '1.25rem', fontWeight: 600, mb: 1, mt: 1 },
                      '& table': { width: '100%', borderCollapse: 'collapse', mb: 2 },
                      '& th': { border: '1px solid #ddd', padding: '8px', backgroundColor: '#f5f5f5', fontWeight: 600, textAlign: 'left' },
                      '& td': { border: '1px solid #ddd', padding: '8px' },
                      '& strong': { fontWeight: 600 }
                    }}
                  >
                    <ReactMarkdown remarkPlugins={[remarkGfm]}>{doc.content}</ReactMarkdown>
                  </Box>
                </Paper>
                {index < documents.length - 1 && <Divider sx={{ my: 2 }} />}
              </Box>
            ))}
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ borderTop: '1px solid #e0e0e0', p: 2 }}>
        <Button
          onClick={onClose}
          variant="contained"
          color="primary"
          sx={{
            textTransform: 'none',
            fontWeight: 600,
            minWidth: '120px'
          }}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default RecruitmentPackModal;
